import React, { useEffect, useRef } from "react";
import { toast } from "sonner";
import { Share2 } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useSortByTeam } from "../lib/SortByTeamContext";

// Link to EXACTLY what's on screen: the page + the team currently selected for it.
export function useShareUrl() {
    const loc = useLocation();
    const { surfTeam, nhlSurfTeam } = useSortByTeam();
    return () => {
        const u = new URL(window.location.origin + loc.pathname + loc.search);
        const nhl = loc.pathname.startsWith("/nhl");
        const team = nhl ? nhlSurfTeam : surfTeam;
        if (team && !u.searchParams.get("team")) u.searchParams.set("team", team);
        if (nhl) u.searchParams.set("league", "nhl");
        return u.toString();
    };
}

export async function sharePage(url) {
    const title = document.title || "Rosterly";
    if (navigator.share) {
        try { await navigator.share({ title, text: title, url }); } catch { /* cancelled */ }
        return;
    }
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Couldn't copy the link"); }
}

// Nav tab-strip chip (desktop).
export function SharePageChip() {
    const getUrl = useShareUrl();
    return (
        <button type="button" onClick={() => sharePage(getUrl())} data-testid="nav-share-page" title="Share this page"
            className="group flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-colors border border-white/10 text-[#94A3B8] hover:text-white hover:border-white/25">
            <Share2 className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
            <span className="text-[9px] font-bold uppercase tracking-wider leading-none whitespace-nowrap">Share Page</span>
        </button>
    );
}

// Mobile tab-row icon.
export function SharePageIcon() {
    const getUrl = useShareUrl();
    return (
        <button type="button" onClick={() => sharePage(getUrl())} data-testid="mobile-tab-share-page" aria-label="Share page" title="Share page"
            className="relative inline-flex items-center justify-center rounded-full border h-9 w-9 transition-colors select-none border-white/12 text-[#94A3B8] active:text-white active:border-white/25">
            <Share2 className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
        </button>
    );
}

// Opening a shared link selects the shared team (applied after the page's own defaults).
export function SharedTeamApplier() {
    const loc = useLocation();
    const { setSurfTeam, setNhlSurfTeam } = useSortByTeam();
    const done = useRef("");
    useEffect(() => {
        const team = new URLSearchParams(loc.search).get("team");
        const key = `${loc.pathname}?${team}`;
        if (!team || done.current === key) return undefined;
        done.current = key;
        const t = setTimeout(() => {
            if (loc.pathname.startsWith("/nhl")) setNhlSurfTeam(team.toUpperCase());
            else setSurfTeam(team.toLowerCase());
        }, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loc.pathname, loc.search]);
    return null;
}
